import React, { useState, useEffect, useMemo } from "react";
import { createPortal } from "react-dom";
import styled, { keyframes } from "styled-components";
import ReactSelect from "react-select";
import {
  Plus,
  Search,
  Trash2,
  Edit2,
  ChevronDown,
  ChevronUp,
  Calendar,
  MapPin,
  CheckCircle,
  XCircle,
  Inbox,
  ClipboardList,
  SearchX,
  Sparkles,
  Users,
  X,
  Loader2,
  IndianRupee,
  FileText,
  Tag,
  Layers,
  MessageSquare,
  ArrowRight,
  Briefcase,
  AlertTriangle,
} from "lucide-react";
import {
  getPublicRequirements,
  getMyRequirements,
  createRequirement,
  updateRequirement,
  deleteRequirement,
  getReceivedApplications,
  updateApplication,
  MarketplaceRequirement,
  RequirementApplication,
  RequirementType,
  ApplicationStatus,
  getMarketplaceProfile,
  MarketplaceProfile,
} from "../../../../Redux/ApiCalls/Dashboard/MarketplaceAPI";
import { showSuccessToast, showErrorToast } from "../../../atoms/Utlis/Toast";
import BrandDatePicker from "../../../atoms/Utlis/BrandDatePicker";
import { device } from "../../../atoms/Utlis/media";
import DeletePopup from "../../../atoms/Dashboard/Folder/DeletePopup";
import { usePermissions } from "../../../../hooks/usePermissions";
import PermissionGate from "../../../atoms/Utlis/PermissionGate";
import MarketplaceEmptyState, { MarketplaceLoadingSpinner } from "./MarketplaceEmptyState";
import {
  APP_STATUS_LABEL,
  REQUIREMENT_TYPE_BADGE_STYLES,
  REQUIREMENT_STATUS_BADGE_STYLES,
} from "./marketplaceConstants";
import {
  PageContainer,
  Hero,
  HeroGlow,
  HeroContent,
  HeroText,
  HeroBadge,
  PageTitle,
  PageSubtitle,
  Panel,
  Toolbar,
  SearchControl,
  ClearButton,
  SecondaryButton,
  ModalActions,
  PrimaryButton,
  FieldInput,
  FieldTextArea,
  FieldSelect,
} from "../quotationMasterListShared";
import { ToolbarLeft, ToolbarRight } from "../documentSharedStyles";
import {
  ListHeader,
  ListTitle,
  CountBadge,
} from "../../../atoms/Dashboard/CustomerTransaction/transactionTableStyles";
import { SubmitButton } from "../../../molecules/Dashboard/Member/memberFormStyles";
import {
  ModalOverlay,
  ModalShell as BaseModalShell,
  ModalHero,
  HeroGlow as ModalHeroGlow,
  HeroRow,
  HeroText as ModalHeroText,
  HeroBadge as ModalHeroBadge,
  ModalTitle,
  ModalSubtitle,
  CloseBtn,
  ModalBody,
  FormCard,
  CardLabel,
} from "../../../molecules/Dashboard/EventCalendar/eventModalSharedStyles";
import { Field, FieldGrid, Label, FormLabelIcon } from "../../../molecules/Dashboard/Member/memberFormStyles";

const fadeUp = keyframes`
  from { opacity: 0; transform: translateY(10px); }
  to { opacity: 1; transform: translateY(0); }
`;

const spin = keyframes`
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
`;

const Spinner = styled(Loader2)`
  animation: ${spin} 0.8s linear infinite;
`;

const gradient =
  "linear-gradient(144.01deg, rgba(255, 201, 81, 0.8) 0%, rgba(206, 86, 176, 0.8) 100%)";

const isLoggedIn = () => !!localStorage.getItem("access_token");

interface ReqForm {
  requirement_type: RequirementType;
  title: string;
  description: string;
  location: string;
  date_from: string;
  date_to: string;
  budget: string;
  status: "active" | "draft";
}

const defaultForm: ReqForm = {
  requirement_type: "equipment_requirement",
  title: "",
  description: "",
  location: "",
  date_from: "",
  date_to: "",
  budget: "",
  status: "active",
};

const RequirementsPage = styled(PageContainer)`
  height: auto;
  min-height: 100%;
  overflow: visible;
  flex-shrink: 0;
`;

const RequirementsPanel = styled(Panel)`
  flex: none;
  min-height: unset;
  overflow: visible;
`;

const APP_STATUS_COLORS: Record<string, string> = {
  pending: "#f09a3e",
  accepted: "#16a34a",
  rejected: "#ef4444",
  withdrawn: "#6b7280",
};

const MarketplaceRequirementsPage: React.FC = () => {
  const { hasPermission } = usePermissions();
  const canCrud = hasPermission("marketplace.crud");
  const [publicReqs, setPublicReqs] = useState<MarketplaceRequirement[]>([]);
  const [myReqs, setMyReqs] = useState<MarketplaceRequirement[]>([]);
  const [loadingPublic, setLoadingPublic] = useState(true);
  const [loadingMine, setLoadingMine] = useState(false);
  const [tab, setTab] = useState<"browse" | "mine" | "applications">("browse");
  const [search, setSearch] = useState("");
  const [typeFilter, setTypeFilter] = useState<RequirementType | "">("");
  const [showForm, setShowForm] = useState(false);
  const [editTarget, setEditTarget] = useState<MarketplaceRequirement | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<MarketplaceRequirement | null>(null);
  const [form, setForm] = useState<ReqForm>(defaultForm);
  const [submitting, setSubmitting] = useState(false);
  const [expanded, setExpanded] = useState<string | null>(null);
  const [receivedApps, setReceivedApps] = useState<RequirementApplication[]>([]);
  const [loadingApps, setLoadingApps] = useState(false);
  const [updatingAppId, setUpdatingAppId] = useState<string | null>(null);
  const [appOwnerNotes, setAppOwnerNotes] = useState<Record<string, string>>({});
  const [hasProfile, setHasProfile] = useState<boolean | null>(null);
  const [myProfile, setMyProfile] = useState<MarketplaceProfile | null>(null);
  const loggedIn = isLoggedIn();

  const checkProfile = async () => {
    if (!loggedIn) return;
    try {
      const res = await getMarketplaceProfile();
      setHasProfile(!!res?.data);
      if (res?.data) setMyProfile(res.data);
    } catch {
      setHasProfile(false);
      setMyProfile(null);
    }
  };

  useEffect(() => {
    checkProfile();
  }, [loggedIn]);

  const fetchPublic = async () => {
    setLoadingPublic(true);
    try {
      const res = await getPublicRequirements({
        requirement_type: typeFilter || undefined,
        search: search || undefined,
        page_size: 50,
      });
      setPublicReqs(res.results ?? []);
    } catch {
      setPublicReqs([]);
    } finally {
      setLoadingPublic(false);
    }
  };

  const fetchMine = async () => {
    if (!loggedIn) return;
    setLoadingMine(true);
    try {
      const res = await getMyRequirements({ page_size: 50 });
      setMyReqs(res.results ?? []);
    } catch {
      setMyReqs([]);
    } finally {
      setLoadingMine(false);
    }
  };

  const fetchApps = async () => {
    setLoadingApps(true);
    try {
      const res = await getReceivedApplications({ page_size: 50 });
      setReceivedApps(res.results ?? []);
    } catch {
      setReceivedApps([]);
    } finally {
      setLoadingApps(false);
    }
  };

  useEffect(() => {
    fetchPublic();
  }, [typeFilter, search]);

  useEffect(() => {
    if (tab === "mine") fetchMine();
  }, [tab]);

  useEffect(() => {
    if (tab === "applications") fetchApps();
  }, [tab]);

  useEffect(() => {
    if (loggedIn) {
      fetchMine();
      fetchApps();
    }
  }, [loggedIn]);

  const openCreate = () => {
    if (hasProfile === false) {
      showErrorToast("Please create a marketplace profile first!");
      return;
    }
    setEditTarget(null);
    setForm(defaultForm);
    setShowForm(true);
  };

  const openEdit = (r: MarketplaceRequirement) => {
    setEditTarget(r);
    setForm({
      requirement_type: r.requirement_type,
      title: r.title,
      description: r.description || "",
      location: r.location || "",
      date_from: r.date_from || "",
      date_to: r.date_to || "",
      budget: r.budget || "",
      status: r.status === "active" ? "active" : "draft",
    });
    setShowForm(true);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (myProfile?.is_active === false) {
      showErrorToast("Your marketplace account is frozen or deactivated. You cannot post or update requirements.");
      return;
    }
    if (!form.title.trim()) {
      showErrorToast("Title is required");
      return;
    }
    setSubmitting(true);
    try {
      const payload: Partial<MarketplaceRequirement> = {
        requirement_type: form.requirement_type,
        title: form.title.trim(),
        description: form.description || undefined,
        location: form.location || undefined,
        date_from: form.date_from || undefined,
        date_to: form.date_to || undefined,
        budget: form.budget || undefined,
        status: form.status,
      };
      if (editTarget) {
        await updateRequirement(editTarget.id, payload);
        showSuccessToast("Requirement updated");
      } else {
        await createRequirement(payload);
        showSuccessToast("Requirement posted");
      }
      setShowForm(false);
      fetchPublic();
      fetchMine();
    } catch (err: any) {
      showErrorToast(err?.response?.data?.message || "Failed to save requirement");
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    if (myProfile?.is_active === false) {
      showErrorToast("Your marketplace account is frozen or deactivated. You cannot delete requirements.");
      setDeleteTarget(null);
      return;
    }
    try {
      await deleteRequirement(deleteTarget.id);
      showSuccessToast("Requirement deleted");
      setDeleteTarget(null);
      fetchPublic();
      fetchMine();
    } catch {
      showErrorToast("Failed to delete");
    }
  };

  const handleMarkFulfilled = async (r: MarketplaceRequirement) => {
    if (myProfile?.is_active === false) {
      showErrorToast("Your marketplace account is frozen or deactivated. You cannot update requirements.");
      return;
    }
    try {
      await updateRequirement(r.id, { status: "fulfilled" } as any);
      showSuccessToast("Marked as fulfilled");
      fetchMine();
    } catch {
      showErrorToast("Failed to update");
    }
  };

  const handleAppUpdate = async (id: string, status: ApplicationStatus, notes?: string) => {
    setUpdatingAppId(id);
    try {
      await updateApplication(id, { status, owner_notes: notes ?? "" });
      showSuccessToast(`Application ${status}!`);
      fetchApps();
    } catch (err: any) {
      showErrorToast(err?.response?.data?.detail ?? "Failed to update");
    } finally {
      setUpdatingAppId(null);
    }
  };

  const displayList = tab === "mine" ? myReqs : publicReqs;
  const hasActiveFilters = !!(search || typeFilter);
  const isMine = tab === "mine";
  const isLoading =
    (tab === "browse" && loadingPublic) ||
    (tab === "mine" && loadingMine) ||
    (tab === "applications" && loadingApps);

  const stats = useMemo(
    () => ({
      browse: publicReqs.length,
      mine: myReqs.length,
      pendingApps: receivedApps.filter((a) => a.status === "pending").length,
      active: [...publicReqs, ...myReqs].filter((r) => r.status === "active").length,
    }),
    [publicReqs, myReqs, receivedApps]
  );

  const getTypeStyle = (type: RequirementType) =>
    REQUIREMENT_TYPE_BADGE_STYLES[type] ?? { bg: "#6b728018", color: "#6b7280" };

  const getStatusStyle = (status: string) =>
    REQUIREMENT_STATUS_BADGE_STYLES[status] ?? { bg: "#6b728018", color: "#6b7280" };

  const renderRequirementCard = (req: MarketplaceRequirement) => {
    const typeStyle = getTypeStyle(req.requirement_type);
    const statusStyle = getStatusStyle(req.status);
    const isOpen = expanded === req.id;

    return (
      <ReqCard key={req.id}>
        <ReqHeader onClick={() => setExpanded(isOpen ? null : req.id)}>
          <ReqHeaderLeft>
            <TypeBadge $bg={typeStyle.bg} $color={typeStyle.color}>
              {req.requirement_type === "equipment_requirement" ? "Equipment" : "Service"}
            </TypeBadge>
            <ReqTitle>{req.title}</ReqTitle>
          </ReqHeaderLeft>
          <ReqHeaderRight>
            <StatusBadge $bg={statusStyle.bg} $color={statusStyle.color}>
              {req.status}
            </StatusBadge>
            <ChevronWrap $open={isOpen}>
              {isOpen ? (
                <ChevronUp size={16} color="rgba(23,23,23,0.4)" />
              ) : (
                <ChevronDown size={16} color="rgba(23,23,23,0.4)" />
              )}
            </ChevronWrap>
          </ReqHeaderRight>
        </ReqHeader>

        {isOpen && (
          <ReqBody>
            <DetailGrid>
              {req.location && (
                <DetailTile $tone="#7c4dff">
                  <TileIcon $tone="#7c4dff">
                    <MapPin size={14} />
                  </TileIcon>
                  <TileContent>
                    <TileLabel>Location</TileLabel>
                    <TileValue>{req.location}</TileValue>
                  </TileContent>
                </DetailTile>
              )}
              {req.date_from && (
                <DetailTile $tone="#35b7c8">
                  <TileIcon $tone="#35b7c8">
                    <Calendar size={14} />
                  </TileIcon>
                  <TileContent>
                    <TileLabel>Date Range</TileLabel>
                    <TileValue>
                      {req.date_from}
                      {req.date_to ? ` → ${req.date_to}` : ""}
                    </TileValue>
                  </TileContent>
                </DetailTile>
              )}
              {req.budget && (
                <DetailTile $tone="#c94daa" $span={!req.location && !req.date_from}>
                  <TileIcon $tone="#c94daa">
                    <IndianRupee size={14} />
                  </TileIcon>
                  <TileContent>
                    <TileLabel>Budget</TileLabel>
                    <PriceValue>₹{Number(req.budget).toLocaleString("en-IN")}</PriceValue>
                  </TileContent>
                </DetailTile>
              )}
            </DetailGrid>

            {req.poster_name && (
              <PosterStrip>
                <PosterAvatar>
                  {req.poster_name.charAt(0).toUpperCase()}
                </PosterAvatar>
                <PosterMeta>
                  <PosterName>{req.poster_name}</PosterName>
                  <PosterSub>
                    {req.poster_profile_type === "studio" ? "Studio" : "Freelancer"}
                  </PosterSub>
                </PosterMeta>
              </PosterStrip>
            )}

            {req.description && (
              <QuoteBox>
                <QuoteIcon>
                  <MessageSquare size={15} />
                </QuoteIcon>
                <QuoteContent>
                  <QuoteLabel>Description</QuoteLabel>
                  <QuoteText>{req.description}</QuoteText>
                </QuoteContent>
              </QuoteBox>
            )}

            {tab === "mine" && canCrud && (
              <ActionFooter>
                <ActionFooterLabel>Manage requirement</ActionFooterLabel>
                <ReqActions>
                  <SmallBtn onClick={() => openEdit(req)}>
                    <Edit2 size={13} /> Edit
                  </SmallBtn>
                  {req.status === "active" && (
                    <SmallBtn $color="#16a34a" onClick={() => handleMarkFulfilled(req)}>
                      <CheckCircle size={13} /> Mark Fulfilled
                    </SmallBtn>
                  )}
                  <SmallBtn $color="#ef4444" onClick={() => setDeleteTarget(req)}>
                    <Trash2 size={13} /> Delete
                  </SmallBtn>
                </ReqActions>
              </ActionFooter>
            )}
          </ReqBody>
        )}
      </ReqCard>
    );
  };

  const renderApplicationCard = (app: RequirementApplication) => {
    const typeStyle = app.requirement_type
      ? getTypeStyle(app.requirement_type)
      : { bg: "#6b728018", color: "#6b7280" };
    const statusColor = APP_STATUS_COLORS[app.status] || "#6b7280";
    const isUpdating = updatingAppId === app.id;
    const initials = (app.applicant_name || "?")
      .split(" ")
      .slice(0, 2)
      .map((w) => w[0]?.toUpperCase() || "")
      .join("");

    return (
      <AppCard key={app.id}>
        <AppCardHeader>
          <AppCardLeft>
            {app.requirement_type && (
              <TypeBadge $bg={typeStyle.bg} $color={typeStyle.color}>
                {app.requirement_type === "equipment_requirement" ? "Equipment" : "Service"}
              </TypeBadge>
            )}
            <AppReqTitle>{app.requirement_title ?? "Recruitment"}</AppReqTitle>
          </AppCardLeft>
          <StatusBadge $bg={`${statusColor}18`} $color={statusColor}>
            {APP_STATUS_LABEL[app.status] || app.status}
          </StatusBadge>
        </AppCardHeader>

        <AppCardBody>
          <ApplicantStrip>
            <PosterAvatar>{initials || "?"}</PosterAvatar>
            <PosterMeta>
              <PosterName>{app.applicant_name || "Applicant"}</PosterName>
              {app.applicant_phone && (
                <WhatsAppChip
                  href={`https://wa.me/${app.applicant_phone.replace(/\D/g, "")}`}
                  target="_blank"
                  rel="noreferrer"
                >
                  {app.applicant_phone}
                  <ArrowRight size={12} />
                </WhatsAppChip>
              )}
            </PosterMeta>
          </ApplicantStrip>

          {app.proposed_rate && (
            <DetailTile $tone="#c94daa" $span>
              <TileIcon $tone="#c94daa">
                <IndianRupee size={14} />
              </TileIcon>
              <TileContent>
                <TileLabel>Proposed Rate</TileLabel>
                <PriceValue>₹{Number(app.proposed_rate).toLocaleString("en-IN")}</PriceValue>
              </TileContent>
            </DetailTile>
          )}

          {app.message && (
            <QuoteBox>
              <QuoteIcon>
                <MessageSquare size={15} />
              </QuoteIcon>
              <QuoteContent>
                <QuoteLabel>Applicant message</QuoteLabel>
                <QuoteText>"{app.message}"</QuoteText>
              </QuoteContent>
            </QuoteBox>
          )}

          {app.status === "pending" && canCrud && (
            <ActionFooter>
              <ActionFooterLabel>Review application</ActionFooterLabel>
              <AppNoteInput
                placeholder="Owner notes (optional)…"
                value={appOwnerNotes[app.id] ?? ""}
                onChange={(e) => setAppOwnerNotes((p) => ({ ...p, [app.id]: e.target.value }))}
              />
              <AppBtnRow>
                <AppAcceptBtn
                  disabled={isUpdating}
                  onClick={() => handleAppUpdate(app.id, "accepted", appOwnerNotes[app.id])}
                >
                  {isUpdating ? <Spinner size={14} /> : <CheckCircle size={14} />}
                  Accept
                </AppAcceptBtn>
                <AppRejectBtn
                  disabled={isUpdating}
                  onClick={() => handleAppUpdate(app.id, "rejected", appOwnerNotes[app.id])}
                >
                  {isUpdating ? <Spinner size={14} /> : <XCircle size={14} />}
                  Reject
                </AppRejectBtn>
              </AppBtnRow>
            </ActionFooter>
          )}
        </AppCardBody>
      </AppCard>
    );
  };

  const listTitle =
    tab === "browse"
      ? "Community requirements"
      : tab === "mine"
        ? "My requirements"
        : "Applications received";

  const listCount =
    tab === "browse"
      ? publicReqs.length
      : tab === "mine"
        ? myReqs.length
        : receivedApps.length;

  return (
    <RequirementsPage>
      <Hero>
        <HeroGlow />
        <HeroContent>
          <HeroText>
            <HeroBadge>
              <Sparkles size={11} />
              Marketplace
            </HeroBadge>
            <PageTitle className="linear-text">Requirements</PageTitle>
            <PageSubtitle>
              Post equipment or service needs, browse community posts, and review applications.
            </PageSubtitle>
          </HeroText>
          {loggedIn && canCrud && (
            <SubmitButton
              type="button"
              $ready
              onClick={() => {
                if (myProfile?.is_active === false) {
                  showErrorToast("Your marketplace account is frozen or deactivated.");
                  return;
                }
                openCreate();
              }}
              style={{
                minWidth: 190,
                height: 46,
                display: "inline-flex",
                alignItems: "center",
                gap: 8,
                opacity: myProfile?.is_active === false ? 0.5 : 1,
                cursor: myProfile?.is_active === false ? "not-allowed" : "pointer",
              }}
              disabled={myProfile?.is_active === false}
            >
              <Plus size={16} /> Post Requirement
            </SubmitButton>
          )}
        </HeroContent>
      </Hero>

      {myProfile?.is_active === false && (
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: "12px",
            padding: "14px 18px",
            marginBottom: "20px",
            borderRadius: "10px",
            background: "rgba(239, 68, 68, 0.12)",
            border: "1px solid rgba(239, 68, 68, 0.35)",
            color: "#fca5a5",
            fontSize: "14px",
            lineHeight: "1.5",
          }}
        >
          <AlertTriangle size={20} style={{ flexShrink: 0, color: "#ef4444" }} />
          <div>
            <strong>Account Frozen:</strong> Your marketplace account has been deactivated/frozen by an administrator.
            You cannot post new requirements or manage applications.
          </div>
        </div>
      )}

      <StatsGrid $columns={loggedIn ? 4 : 2}>
        <WalletHero>
          <WalletIconWrap>
            <Layers size={16} />
          </WalletIconWrap>
          <WalletHeroBody>
            <WalletHeroValue>{stats.browse}</WalletHeroValue>
            <WalletHeroLabel>Browse listings</WalletHeroLabel>
          </WalletHeroBody>
        </WalletHero>

        {loggedIn && (
          <StatCard $tone="#7c4dff">
            <StatIcon $tone="#7c4dff">
              <ClipboardList size={16} />
            </StatIcon>
            <StatBody>
              <StatValue>{stats.mine}</StatValue>
              <StatLabel>My posts</StatLabel>
            </StatBody>
          </StatCard>
        )}

        {loggedIn && (
          <StatCard $tone="#f09a3e">
            <StatIcon $tone="#f09a3e">
              <Inbox size={16} />
            </StatIcon>
            <StatBody>
              <StatValue>{stats.pendingApps}</StatValue>
              <StatLabel>Pending applications</StatLabel>
            </StatBody>
          </StatCard>
        )}

        <StatCard $tone="#31a66a">
          <StatIcon $tone="#31a66a">
            <CheckCircle size={16} />
          </StatIcon>
          <StatBody>
            <StatValue>{stats.active}</StatValue>
            <StatLabel>Active posts</StatLabel>
          </StatBody>
        </StatCard>
      </StatsGrid>

      <TabBar>
        <Tab $active={tab === "browse"} onClick={() => setTab("browse")}>
          <Briefcase size={14} /> Browse All
        </Tab>
        {loggedIn && (
          <Tab $active={tab === "mine"} onClick={() => setTab("mine")}>
            <ClipboardList size={14} /> My Requirements
          </Tab>
        )}
        {loggedIn && (
          <Tab $active={tab === "applications"} onClick={() => setTab("applications")}>
            <Inbox size={14} /> Applications
            {stats.pendingApps > 0 && <TabCount>{stats.pendingApps}</TabCount>}
          </Tab>
        )}
      </TabBar>

      <RequirementsPanel>
        {tab === "browse" && (
          <Toolbar>
            <ToolbarLeft>
              <SearchControl>
                <Search size={16} />
                <input
                  placeholder="Search requirements..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                />
                {search && (
                  <ClearButton type="button" onClick={() => setSearch("")}>
                    <X size={14} />
                  </ClearButton>
                )}
              </SearchControl>
            </ToolbarLeft>
            <ToolbarRight>
              <FilterSelect
                value={typeFilter}
                onChange={(e) => setTypeFilter(e.target.value as RequirementType | "")}
              >
                <option value="">All Types</option>
                <option value="equipment_requirement">Equipment</option>
                <option value="service_requirement">Service / Hire</option>
              </FilterSelect>
            </ToolbarRight>
          </Toolbar>
        )}

        <ListHeader style={{ padding: tab === "browse" ? "0 22px 12px" : "20px 22px 12px" }}>
          <ListTitle>{listTitle}</ListTitle>
          {!isLoading && listCount > 0 && <CountBadge>{listCount} shown</CountBadge>}
        </ListHeader>

        <PanelContent>
          {isLoading ? (
            <MarketplaceLoadingSpinner padding="60px 22px" />
          ) : tab !== "applications" ? (
            displayList.length === 0 ? (
              <MarketplaceEmptyState
                padding="32px 22px 48px"
                icon={
                  isMine ? (
                    <ClipboardList size={36} strokeWidth={1.5} />
                  ) : (
                    <SearchX size={36} strokeWidth={1.5} />
                  )
                }
                title={isMine ? "No requirements yet" : "No requirements found"}
                description={
                  isMine
                    ? "Post what equipment or services you need — studios and freelancers in the community can respond."
                    : hasActiveFilters
                      ? "Nothing matched your search or filters. Try different keywords or broaden your criteria."
                      : "The marketplace is quiet for now. Be the first to post a need and connect with the community."
                }
                actions={[
                  ...(!isMine && hasActiveFilters
                    ? [
                        {
                          label: "Clear filters",
                          onClick: () => {
                            setSearch("");
                            setTypeFilter("");
                          },
                          variant: "secondary" as const,
                        },
                      ]
                    : []),
                  ...(loggedIn && canCrud
                    ? [
                        {
                          label: isMine ? "Post Your First Requirement" : "Post a Requirement",
                          onClick: openCreate,
                          icon: <Plus size={15} />,
                        },
                      ]
                    : []),
                ]}
                hint={
                  !isMine && !hasActiveFilters
                    ? {
                        icon: <Sparkles size={13} />,
                        text: "Equipment rentals, crew hires, gear swaps & more",
                      }
                    : undefined
                }
              />
            ) : (
              <ReqList>{displayList.map(renderRequirementCard)}</ReqList>
            )
          ) : receivedApps.length === 0 ? (
            <MarketplaceEmptyState
              padding="32px 22px 48px"
              icon={<Inbox size={36} strokeWidth={1.5} />}
              title="No applications yet"
              description="When studios or freelancers apply to your requirements, they'll show up here. Keep your posts active and detailed to attract more responses."
              actions={[
                { label: "Post a Requirement", onClick: openCreate, icon: <Plus size={15} /> },
                {
                  label: "View My Requirements",
                  onClick: () => setTab("mine"),
                  variant: "secondary",
                },
              ]}
              hint={{
                icon: <Users size={13} />,
                text: "Applicants can propose rates, leave messages & contact you directly",
              }}
            />
          ) : (
            <AppList>{receivedApps.map(renderApplicationCard)}</AppList>
          )}
        </PanelContent>
      </RequirementsPanel>

      {showForm &&
        createPortal(
          <ModalOverlay onClick={() => setShowForm(false)}>
            <ModalShell onClick={(e) => e.stopPropagation()}>
              <ModalHero>
                <ModalHeroGlow />
                <HeroRow>
                  <ModalHeroText>
                    <ModalHeroBadge>
                      <Sparkles size={11} />
                      Requirement
                    </ModalHeroBadge>
                    <ModalTitle>{editTarget ? "Edit Requirement" : "Post a Requirement"}</ModalTitle>
                    <ModalSubtitle>
                      {editTarget
                        ? "Update your post details and visibility."
                        : "Tell the community what equipment or services you need."}
                    </ModalSubtitle>
                  </ModalHeroText>
                  <CloseBtn type="button" onClick={() => setShowForm(false)} aria-label="Close">
                    <X size={18} />
                  </CloseBtn>
                </HeroRow>
              </ModalHero>

              <ModalBody as="form" id="requirement-form" onSubmit={handleSubmit}>
                <FormCard>
                  <CardLabel>Requirement details</CardLabel>
                  <Field>
                    <Label>Type</Label>
                    <FieldSelect
                      value={form.requirement_type}
                      onChange={(e) =>
                        setForm((p) => ({
                          ...p,
                          requirement_type: e.target.value as RequirementType,
                        }))
                      }
                    >
                      <option value="equipment_requirement">Equipment Requirement</option>
                      <option value="service_requirement">Hire / Service</option>
                    </FieldSelect>
                  </Field>
                  <Field>
                    <Label htmlFor="req-title">
                      <FormLabelIcon icon={Tag} />
                      Title *
                    </Label>
                    <FieldInput
                      id="req-title"
                      value={form.title}
                      onChange={(e) => setForm((p) => ({ ...p, title: e.target.value }))}
                      placeholder="e.g. Need 2 Sony A7IV for wedding"
                      required
                    />
                  </Field>
                  <Field>
                    <Label htmlFor="req-desc">
                      <FormLabelIcon icon={FileText} />
                      Description
                    </Label>
                    <FieldTextArea
                      id="req-desc"
                      value={form.description}
                      onChange={(e) => setForm((p) => ({ ...p, description: e.target.value }))}
                      rows={3}
                      placeholder="Details about what you need..."
                    />
                  </Field>
                </FormCard>

                <FormCard>
                  <CardLabel>Location, budget & dates</CardLabel>
                  <FieldGrid>
                    <Field>
                      <Label htmlFor="req-location">
                        <FormLabelIcon icon={MapPin} />
                        Location
                      </Label>
                      <FieldInput
                        id="req-location"
                        value={form.location}
                        onChange={(e) => setForm((p) => ({ ...p, location: e.target.value }))}
                        placeholder="City"
                      />
                    </Field>
                    <Field>
                      <Label htmlFor="req-budget">
                        <FormLabelIcon icon={IndianRupee} />
                        Budget (₹)
                      </Label>
                      <FieldInput
                        id="req-budget"
                        type="number"
                        value={form.budget}
                        onChange={(e) => setForm((p) => ({ ...p, budget: e.target.value }))}
                        placeholder="e.g. 15000"
                      />
                    </Field>
                  </FieldGrid>
                  <FieldGrid>
                    <Field>
                      <Label htmlFor="req-from">
                        <FormLabelIcon icon={Calendar} />
                        Date From
                      </Label>
                      <BrandDatePicker
                        name="req-from"
                        value={form.date_from}
                        onChange={(v) => setForm((p) => ({ ...p, date_from: v }))}
                      />
                    </Field>
                    <Field>
                      <Label htmlFor="req-to">Date To</Label>
                      <BrandDatePicker
                        name="req-to"
                        value={form.date_to}
                        onChange={(v) => setForm((p) => ({ ...p, date_to: v }))}
                      />
                    </Field>
                  </FieldGrid>
                  <Field>
                    <Label>Status</Label>
                    <FieldSelect
                      value={form.status}
                      onChange={(e) =>
                        setForm((p) => ({ ...p, status: e.target.value as "active" | "draft" }))
                      }
                    >
                      <option value="active">Active (visible publicly)</option>
                      <option value="draft">Draft</option>
                    </FieldSelect>
                  </Field>
                </FormCard>
              </ModalBody>

              <ModalActions style={{ flexDirection: "row", gap: 10 }}>
                <SecondaryButton
                  type="button"
                  onClick={() => setShowForm(false)}
                  style={{ flex: 1 }}
                >
                  Cancel
                </SecondaryButton>
                <PrimaryButton type="submit" form="requirement-form" disabled={submitting} style={{ flex: 1.4 }}>
                  {submitting ? (
                    <>
                      <Spinner size={16} /> Saving…
                    </>
                  ) : editTarget ? (
                    "Update"
                  ) : (
                    "Post"
                  )}
                </PrimaryButton>
              </ModalActions>
            </ModalShell>
          </ModalOverlay>,
          document.body
        )}

      {deleteTarget && (
        <DeletePopup
          text={`Delete "${deleteTarget.title}"?`}
          buttonText="Delete"
          cancel={() => setDeleteTarget(null)}
          Delete={handleDelete}
        />
      )}
    </RequirementsPage>
  );
};

export default MarketplaceRequirementsPage;

const ModalShell = styled(BaseModalShell)`
  max-width: 560px;
`;

const StatsGrid = styled.div<{ $columns?: number }>`
  display: grid;
  grid-template-columns: repeat(${(p) => p.$columns ?? 4}, 1fr);
  gap: 12px;
  flex-shrink: 0;

  @media (max-width: 900px) {
    grid-template-columns: repeat(2, 1fr);
  }

  @media (max-width: 560px) {
    grid-template-columns: 1fr;
  }
`;

const WalletHero = styled.div`
  position: relative;
  overflow: hidden;
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 14px 16px;
  border-radius: 16px;
  color: #fff;
  background: ${gradient};
  box-shadow: 0 8px 22px rgba(201, 77, 170, 0.22);
  text-align: left;

  &::after {
    content: "";
    position: absolute;
    right: -20px;
    bottom: -24px;
    width: 90px;
    height: 90px;
    border-radius: 50%;
    background: rgba(255, 255, 255, 0.12);
  }
`;

const WalletIconWrap = styled.div`
  position: relative;
  z-index: 1;
  width: 36px;
  height: 36px;
  border-radius: 11px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  background: rgba(255, 255, 255, 0.22);
`;

const WalletHeroBody = styled.div`
  position: relative;
  z-index: 1;
  min-width: 0;
`;

const WalletHeroValue = styled.div`
  font-family: Urbanist, sans-serif;
  font-size: 20px;
  font-weight: 800;
  color: #fff;
  line-height: 1.1;
`;

const WalletHeroLabel = styled.div`
  font-family: Montserrat, sans-serif;
  font-size: 11px;
  font-weight: 600;
  color: rgba(255, 255, 255, 0.88);
  margin-top: 2px;
`;

const StatCard = styled.div<{ $tone: string }>`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 14px 16px;
  border-radius: 16px;
  background: #fff;
  border: 1px solid rgba(23, 23, 23, 0.06);
  box-shadow: 0 6px 18px rgba(23, 23, 23, 0.04);
`;

const StatIcon = styled.div<{ $tone: string }>`
  width: 36px;
  height: 36px;
  border-radius: 11px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  color: ${(p) => p.$tone};
  background: ${(p) => `${p.$tone}18`};
`;

const StatBody = styled.div`
  min-width: 0;
`;

const StatValue = styled.div`
  font-family: Urbanist, sans-serif;
  font-size: 20px;
  font-weight: 800;
  color: #171717;
  line-height: 1.1;
`;

const StatLabel = styled.div`
  font-family: Montserrat, sans-serif;
  font-size: 11px;
  font-weight: 600;
  color: rgba(23, 23, 23, 0.5);
  margin-top: 2px;
`;

const TabBar = styled.div`
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
  flex-shrink: 0;
`;

const Tab = styled.button<{ $active: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: 7px;
  padding: 10px 18px;
  border-radius: 12px;
  border: 1px solid ${(p) => (p.$active ? "rgba(201, 77, 170, 0.35)" : "rgba(23, 23, 23, 0.08)")};
  background: ${(p) => (p.$active ? "rgba(201, 77, 170, 0.08)" : "#fff")};
  color: ${(p) => (p.$active ? "#c94daa" : "rgba(23, 23, 23, 0.62)")};
  font-family: Urbanist, sans-serif;
  font-size: 13px;
  font-weight: 700;
  cursor: pointer;
  transition: all 0.15s ease;
  box-shadow: ${(p) => (p.$active ? "0 4px 14px rgba(201, 77, 170, 0.12)" : "0 2px 8px rgba(0,0,0,0.03)")};

  &:hover {
    border-color: rgba(201, 77, 170, 0.28);
    color: #c94daa;
  }
`;

const TabCount = styled.span`
  min-width: 18px;
  height: 18px;
  padding: 0 5px;
  border-radius: 100px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 10px;
  font-weight: 800;
  color: #fff;
  background: #f09a3e;
`;

const requirementFilterSelectStyles = {
  control: (base: any, state: any) => ({
    ...base,
    minHeight: "46px",
    minWidth: "160px",
    borderRadius: "12px",
    borderColor: state.isFocused ? "rgba(201, 77, 170, 0.35)" : "rgba(23, 23, 23, 0.1)",
    background: state.isFocused ? "#ffffff" : "#fafafa",
    boxShadow: state.isFocused ? "0 0 0 3px rgba(201, 77, 170, 0.08)" : "none",
    fontFamily: "Urbanist, sans-serif",
    fontSize: "13px",
    fontWeight: 600,
    color: "#171717",
    cursor: "pointer",
    transition: "border-color 0.15s ease, box-shadow 0.15s ease, background 0.15s ease",
    "&:hover": {
      borderColor: "rgba(201, 77, 170, 0.28)",
      background: "#ffffff",
    },
  }),
  valueContainer: (base: any) => ({ ...base, padding: "0 14px" }),
  input: (base: any) => ({ ...base, margin: 0, padding: 0 }),
  placeholder: (base: any) => ({ ...base, color: "rgba(23, 23, 23, 0.4)", fontWeight: 500 }),
  singleValue: (base: any) => ({ ...base, color: "#171717" }),
  indicatorSeparator: () => ({ display: "none" }),
  dropdownIndicator: (base: any, state: any) => ({
    ...base,
    color: state.isFocused ? "#c94daa" : "rgba(23, 23, 23, 0.45)",
    padding: "0 12px 0 8px",
    transform: state.selectProps.menuIsOpen ? "rotate(180deg)" : "rotate(0deg)",
    transition: "transform 0.15s ease, color 0.15s ease",
  }),
  menu: (base: any) => ({
    ...base,
    marginTop: "8px",
    borderRadius: "16px",
    padding: "6px",
    border: "1px solid rgba(201, 77, 170, 0.12)",
    boxShadow: "0 18px 36px rgba(23, 23, 23, 0.14)",
    overflow: "hidden",
    zIndex: 9999,
  }),
  menuList: (base: any) => ({ ...base, padding: 0 }),
  menuPortal: (base: any) => ({ ...base, zIndex: 9999 }),
  option: (base: any, state: any) => ({
    ...base,
    borderRadius: "12px",
    padding: "11px 13px",
    fontFamily: "Urbanist, sans-serif",
    fontSize: "14px",
    fontWeight: state.isSelected ? 700 : 600,
    color: "#171717",
    background: state.isSelected
      ? "linear-gradient(135deg, rgba(255, 201, 81, 0.18), rgba(201, 77, 170, 0.16))"
      : state.isFocused
        ? "rgba(201, 77, 170, 0.08)"
        : "#ffffff",
  }),
};

type FilterSelectProps = {
  value: string;
  onChange: (e: { target: { value: string } }) => void;
  children: React.ReactNode;
};

const FilterSelect: React.FC<FilterSelectProps> = ({ value, onChange, children }) => {
  const options = React.Children.toArray(children)
    .filter((child): child is React.ReactElement<any> => React.isValidElement<any>(child) && child.type === "option")
    .map((child) => ({ value: String(child.props.value), label: child.props.children }));

  const selectedOption = options.find((option) => option.value === String(value)) ?? null;

  return (
    <ReactSelect
      value={selectedOption}
      onChange={(option) => onChange({ target: { value: option?.value ?? "" } })}
      options={options}
      styles={requirementFilterSelectStyles}
      menuPosition="fixed"
      menuPortalTarget={document.body}
      isSearchable={false}
    />
  );
};

const PanelContent = styled.div`
  flex: none;
  overflow: visible;
  padding: 0 22px 24px;
`;

const ReqList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const ReqCard = styled.div`
  background: #fafafa;
  border: 1px solid rgba(23, 23, 23, 0.06);
  border-radius: 18px;
  overflow: hidden;
  animation: ${fadeUp} 0.3s ease;
  transition: transform 0.2s, box-shadow 0.2s;

  &:hover {
    background: #fff;
    box-shadow: 0 8px 24px rgba(23, 23, 23, 0.06);
  }
`;

const ReqHeader = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
  padding: 16px 18px;
  cursor: pointer;
  transition: background 0.15s ease;

  &:hover {
    background: rgba(201, 77, 170, 0.03);
  }
`;

const ReqHeaderLeft = styled.div`
  flex: 1;
  min-width: 0;
`;

const ReqHeaderRight = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  flex-shrink: 0;
`;

const TypeBadge = styled.span<{ $bg: string; $color: string }>`
  display: inline-block;
  padding: 3px 9px;
  border-radius: 100px;
  font-size: 10px;
  font-weight: 700;
  font-family: Montserrat, sans-serif;
  background: ${(p) => p.$bg};
  color: ${(p) => p.$color};
  margin-bottom: 6px;
  text-transform: uppercase;
  letter-spacing: 0.03em;
`;

const ReqTitle = styled.div`
  font-size: 15px;
  font-weight: 800;
  color: #171717;
  letter-spacing: -0.01em;
`;

const StatusBadge = styled.span<{ $bg: string; $color: string }>`
  padding: 4px 11px;
  border-radius: 100px;
  font-size: 10px;
  font-weight: 700;
  font-family: Montserrat, sans-serif;
  background: ${(p) => p.$bg};
  color: ${(p) => p.$color};
  text-transform: capitalize;
  white-space: nowrap;
`;

const ChevronWrap = styled.span<{ $open: boolean }>`
  display: flex;
  align-items: center;
  transition: transform 0.2s ease;
  transform: ${(p) => (p.$open ? "rotate(0deg)" : "rotate(0deg)")};
`;

const ReqBody = styled.div`
  padding: 0 18px 18px;
  display: flex;
  flex-direction: column;
  gap: 12px;
  border-top: 1px solid rgba(23, 23, 23, 0.05);
  padding-top: 14px;
`;

const DetailGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 10px;

  @media (max-width: 520px) {
    grid-template-columns: 1fr;
  }
`;

const DetailTile = styled.div<{ $tone: string; $span?: boolean }>`
  display: flex;
  align-items: flex-start;
  gap: 10px;
  padding: 11px 12px;
  border-radius: 14px;
  background: #fff;
  border: 1px solid rgba(23, 23, 23, 0.06);
  grid-column: ${(p) => (p.$span ? "1 / -1" : "auto")};
`;

const TileIcon = styled.div<{ $tone: string }>`
  width: 32px;
  height: 32px;
  border-radius: 9px;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${(p) => p.$tone};
  background: ${(p) => `${p.$tone}14`};
`;

const TileContent = styled.div`
  min-width: 0;
  flex: 1;
`;

const TileLabel = styled.div`
  font-family: Montserrat, sans-serif;
  font-size: 10px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: rgba(23, 23, 23, 0.42);
  margin-bottom: 2px;
`;

const TileValue = styled.div`
  font-family: Urbanist, sans-serif;
  font-size: 13px;
  font-weight: 700;
  color: #171717;
`;

const PriceValue = styled.div`
  font-family: Urbanist, sans-serif;
  font-size: 18px;
  font-weight: 800;
  letter-spacing: -0.02em;
  background: linear-gradient(135deg, #ffc951 0%, #c94daa 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
`;

const PosterStrip = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 12px;
  border-radius: 12px;
  background: linear-gradient(135deg, rgba(255, 201, 81, 0.08) 0%, rgba(206, 86, 176, 0.06) 100%);
  border: 1px solid rgba(201, 77, 170, 0.1);
`;

const ApplicantStrip = styled(PosterStrip)``;

const PosterAvatar = styled.div`
  width: 36px;
  height: 36px;
  border-radius: 10px;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  font-family: Urbanist, sans-serif;
  font-size: 13px;
  font-weight: 800;
  color: #fff;
  background: ${gradient};
`;

const PosterMeta = styled.div`
  min-width: 0;
  flex: 1;
`;

const PosterName = styled.div`
  font-size: 14px;
  font-weight: 800;
  color: #171717;
`;

const PosterSub = styled.div`
  font-family: Montserrat, sans-serif;
  font-size: 11px;
  font-weight: 600;
  color: rgba(23, 23, 23, 0.48);
  margin-top: 1px;
`;

const QuoteBox = styled.div`
  display: flex;
  gap: 10px;
  padding: 12px 14px;
  border-radius: 14px;
  background: rgba(124, 77, 255, 0.04);
  border: 1px solid rgba(124, 77, 255, 0.12);
  border-left: 3px solid #7c4dff;
`;

const QuoteIcon = styled.div`
  width: 30px;
  height: 30px;
  border-radius: 8px;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #7c4dff;
  background: rgba(124, 77, 255, 0.1);
`;

const QuoteContent = styled.div`
  min-width: 0;
  flex: 1;
`;

const QuoteLabel = styled.div`
  font-family: Montserrat, sans-serif;
  font-size: 10px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: rgba(124, 77, 255, 0.75);
  margin-bottom: 4px;
`;

const QuoteText = styled.div`
  font-size: 13px;
  font-weight: 500;
  color: rgba(23, 23, 23, 0.78);
  line-height: 1.55;
  font-family: Montserrat, sans-serif;
`;

const ActionFooter = styled.div`
  padding-top: 12px;
  border-top: 1px dashed rgba(23, 23, 23, 0.1);
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const ActionFooterLabel = styled.div`
  font-family: Montserrat, sans-serif;
  font-size: 10px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: rgba(23, 23, 23, 0.38);
`;

const ReqActions = styled.div`
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
`;

const SmallBtn = styled.button<{ $color?: string }>`
  display: inline-flex;
  align-items: center;
  gap: 5px;
  height: 36px;
  padding: 0 14px;
  border: 1px solid ${(p) => (p.$color ? `${p.$color}44` : "rgba(23, 23, 23, 0.12)")};
  border-radius: 10px;
  background: #fff;
  color: ${(p) => p.$color || "rgba(23, 23, 23, 0.72)"};
  font-family: Urbanist, sans-serif;
  font-size: 12px;
  font-weight: 700;
  cursor: pointer;
  transition: all 0.15s ease;

  &:hover {
    background: ${(p) => (p.$color ? `${p.$color}10` : "rgba(201, 77, 170, 0.04)")};
    border-color: ${(p) => (p.$color ? `${p.$color}55` : "rgba(201, 77, 170, 0.25)")};
    transform: translateY(-1px);
  }
`;

const AppList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 14px;
`;

const AppCard = styled.div`
  background: #fafafa;
  border: 1px solid rgba(23, 23, 23, 0.06);
  border-radius: 18px;
  overflow: hidden;
  animation: ${fadeUp} 0.3s ease;
  transition: transform 0.2s, box-shadow 0.2s;

  &:hover {
    background: #fff;
    box-shadow: 0 8px 24px rgba(23, 23, 23, 0.06);
  }
`;

const AppCardHeader = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
  padding: 16px 18px 10px;
  background: linear-gradient(180deg, rgba(201, 77, 170, 0.04) 0%, transparent 100%);
`;

const AppCardLeft = styled.div`
  flex: 1;
  min-width: 0;
`;

const AppReqTitle = styled.div`
  font-size: 15px;
  font-weight: 800;
  color: #171717;
  letter-spacing: -0.01em;
`;

const AppCardBody = styled.div`
  padding: 0 18px 18px;
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const WhatsAppChip = styled.a`
  display: inline-flex;
  align-items: center;
  gap: 5px;
  margin-top: 4px;
  padding: 3px 10px;
  border-radius: 100px;
  font-size: 11px;
  font-weight: 700;
  color: #16a34a;
  background: rgba(22, 163, 74, 0.08);
  border: 1px solid rgba(22, 163, 74, 0.18);
  text-decoration: none;
  transition: all 0.15s ease;
  width: fit-content;

  &:hover {
    background: rgba(22, 163, 74, 0.14);
    transform: translateX(2px);
  }
`;

const AppNoteInput = styled.input`
  width: 100%;
  box-sizing: border-box;
  padding: 11px 14px;
  border: 1px solid rgba(23, 23, 23, 0.08);
  border-radius: 12px;
  font-family: Urbanist, sans-serif;
  font-size: 13px;
  font-weight: 600;
  background: #fafafa;
  color: #171717;
  outline: none;
  transition: border-color 0.15s ease, box-shadow 0.15s ease;

  &:focus {
    border-color: rgba(201, 77, 170, 0.4);
    box-shadow: 0 0 0 3px rgba(201, 77, 170, 0.1);
    background: #fff;
  }
`;

const AppBtnRow = styled.div`
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
`;

const AppAcceptBtn = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  height: 38px;
  padding: 0 18px;
  border-radius: 10px;
  border: none;
  cursor: pointer;
  background: #16a34a;
  color: white;
  font-family: Urbanist, sans-serif;
  font-size: 13px;
  font-weight: 700;
  box-shadow: 0 4px 12px rgba(22, 163, 74, 0.25);
  transition: all 0.15s ease;

  &:hover:not(:disabled) {
    transform: translateY(-1px);
  }

  &:disabled {
    opacity: 0.55;
    cursor: not-allowed;
  }
`;

const AppRejectBtn = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  height: 38px;
  padding: 0 18px;
  border-radius: 10px;
  border: none;
  cursor: pointer;
  background: #ef4444;
  color: white;
  font-family: Urbanist, sans-serif;
  font-size: 13px;
  font-weight: 700;
  box-shadow: 0 4px 12px rgba(239, 68, 68, 0.25);
  transition: all 0.15s ease;

  &:hover:not(:disabled) {
    transform: translateY(-1px);
  }

  &:disabled {
    opacity: 0.55;
    cursor: not-allowed;
  }
`;
